<script setup lang="ts">
import { ref } from 'vue';
import type { Customer } from '@/types/customer';
import SearchBar from '@/components/ui/SearchBar.vue';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';

interface Props {
  searchQuery: string;
  searchResults: Customer[];
  isSearching: boolean;
  showCreateForm: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:searchQuery': [value: string];
  'search': [];
  'select-customer': [customer: Customer];
  'show-create-form': [show: boolean];
}>();

const handleSearchInput = () => {
  emit('search');
};

const selectCustomer = (customer: Customer) => {
  emit('select-customer', customer);
};

const showCreateCustomerForm = () => {
  emit('show-create-form', true);
};
</script>

<template>
  <div class="space-y-4">
    <!-- Quick Actions -->
    <div class="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
      <button
        class="w-full sm:flex-1 px-3 sm:px-4 py-2 sm:py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-md transition-colors flex items-center justify-center text-sm sm:text-base"
        @click="showCreateCustomerForm"
      >
        <svg
          class="w-4 h-4 sm:w-5 sm:h-5 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        <span class="truncate">Crear Nuevo Cliente</span>
      </button>
    </div>

    <!-- Search Input -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Buscar cliente por nombre o teléfono
      </label>
      <SearchBar
        :model-value="searchQuery"
        placeholder="Escribe nombre o teléfono..."
        variant="default"
        size="md"
        :show-clear="true"
        @update:model-value="$emit('update:searchQuery', $event); handleSearchInput()"
        @search="handleSearchInput"
      />
    </div>

    <!-- Loading State -->
    <LoadingSpinner
      v-if="isSearching"
      size="sm"
      color="primary"
      text="Buscando..."
    />

    <!-- Search Results -->
    <div
      v-else-if="searchResults && searchResults.length > 0"
      class="space-y-3"
    >
      <div class="text-sm text-gray-600 dark:text-gray-400 mb-2">
        {{ searchResults.length }} cliente(s) encontrado(s)
      </div>
      
      <div
        v-for="customer in searchResults"
        :key="customer.id"
        class="p-3 sm:p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors active:scale-[0.98]"
        @click="selectCustomer(customer)"
      >
        <div class="flex justify-between items-start">
          <div class="flex-1 min-w-0">
            <h4 class="font-medium text-gray-900 dark:text-gray-100 truncate">
              {{ customer.name }}
            </h4>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ customer.phone }}
            </p>
            <p
              v-if="customer.full_address && customer.full_address !== 'Sin dirección'"
              class="text-sm text-gray-500 dark:text-gray-400 mt-1 truncate"
            >
              {{ customer.full_address }}
            </p>
          </div>
          <div class="ml-2 sm:ml-4 flex-shrink-0">
            <svg
              class="w-5 h-5 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- No Results -->
    <div
      v-else-if="searchQuery && !isSearching"
      class="text-center py-4"
    >
      <svg
        class="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
        />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
        No se encontraron clientes
      </h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Intenta con otra búsqueda o crea un nuevo cliente
      </p>
      <button
        class="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-md transition-colors"
        @click="showCreateCustomerForm"
      >
        Crear Nuevo Cliente
      </button>
    </div>
  </div>
</template>
